import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Button, SelectField, TextField } from '../ui/controls';
import { ConfirmDialog } from '../ui/dialog';
import { ErrorNotice, Notice, useToast } from '../ui/feedback';
import { DataTable, PageHead, Pager, Tabs, useKeysetPage } from '../ui/table';
import { DefList, ItemId, Num, Position, Tag, Time, UserStatusTag, Uuid, VerifiedTag } from '../ui/values';
import { ApiError } from '../lib/api';

/** Nur im Dev-Modus (router.tsx): alle Komponenten auf einer Seite, für Sichtprüfung und Screenshots. */

interface Row {
  id: string;
  name: string;
  owner: string;
  dim: string;
  pos: [number, number, number];
  count: number;
  at: string;
}

const now = Date.now();
const rows: Row[] = [
  { id: '1', name: 'Erze', owner: 'Steve', dim: 'minecraft:overworld', pos: [120, 64, -33], count: 1236, at: new Date(now - 180_000).toISOString() },
  { id: '2', name: 'Lohen', owner: 'Alex', dim: 'minecraft:the_nether', pos: [-8, 31, 402], count: 12, at: new Date(now - 7_200_000).toISOString() },
  { id: '3', name: 'Perlen', owner: 'Notch', dim: 'minecraft:the_end', pos: [1000, 70, 0], count: 64, at: new Date(now - 3 * 86_400_000).toISOString() },
  { id: '4', name: 'Twilight', owner: 'Steve', dim: 'twilightforest:twilight_forest', pos: [5, 5, 5], count: 3, at: new Date(now - 40_000).toISOString() },
];

export function UiPage() {
  const toast = useToast();
  const page = useKeysetPage();
  const [tab, setTab] = useState<'inhalt' | 'verlauf'>('inhalt');
  const [confirm, setConfirm] = useState(false);

  return (
    <main className="stack-lg" style={{ maxWidth: 1100 }}>
      <PageHead
        title="Komponenten"
        context="Interne Musterseite · nur im Dev-Modus"
        actions={
          <>
            <Button icon={<Search aria-hidden />}>Sekundär</Button>
            <Button variant="primary" icon={<Plus aria-hidden />} onClick={() => toast('Gespeichert.')}>
              Primär
            </Button>
          </>
        }
      />

      <section className="stack">
        <h2>Buttons</h2>
        <div className="row-actions">
          <Button variant="primary">Primär</Button>
          <Button>Sekundär</Button>
          <Button variant="quiet">Leise</Button>
          <Button variant="danger" onClick={() => setConfirm(true)}>
            Löschen…
          </Button>
          <Button busy>Speichern</Button>
          <Button disabled>Gesperrt</Button>
        </div>
      </section>

      <section className="stack">
        <h2>Felder</h2>
        <div className="row-actions" style={{ alignItems: 'flex-start' }}>
          <TextField label="Beta-Code" placeholder="XXXX-XXXX-XXXX" help="12 Zeichen, Bindestriche optional." />
          <TextField label="Teamname" defaultValue="" error="Bitte einen Namen mit 1–32 Zeichen eingeben." />
          <SelectField label="Status" defaultValue="active">
            <option value="active">aktiv</option>
            <option value="pending_beta">Beta ausstehend</option>
          </SelectField>
        </div>
      </section>

      <section className="stack">
        <h2>Werte und Tags</h2>
        <div className="row-actions">
          <UserStatusTag status="active" />
          <UserStatusTag status="pending_beta" />
          <UserStatusTag status="suspended" />
          <VerifiedTag verified />
          <VerifiedTag verified={false} />
          <Tag tone="neutral">widerrufen</Tag>
        </div>
        <DefList
          items={[
            ['MC-UUID', <Uuid value="069a79f4-44e9-4726-a5be-fca90e38a3ef" />],
            ['Item', <ItemId id="minecraft:diamond" />],
            ['Position', <Position dimension="minecraft:the_nether" pos={[-8, 31, 402]} />],
            ['Menge', <Num value={1236} />],
            ['Aktualisiert', <Time iso={rows[0]!.at} absolute />],
          ]}
        />
      </section>

      <section className="stack">
        <h2>Tabelle</h2>
        <DataTable
          label="Beispielkisten"
          rows={rows}
          rowKey={(r) => r.id}
          rowHref={() => '/_ui'}
          empty="Keine Kisten."
          columns={[
            { key: 'name', header: 'Name', cell: (r) => r.name },
            { key: 'owner', header: 'Besitzer', cell: (r) => r.owner },
            { key: 'pos', header: 'Position', cell: (r) => <Position dimension={r.dim} pos={r.pos} /> },
            { key: 'count', header: 'Menge', num: true, cell: (r) => <Num value={r.count} /> },
            { key: 'at', header: 'Aktualisiert', hide: 'sm', cell: (r) => <Time iso={r.at} /> },
          ]}
        />
        <Pager page={page} count={rows.length} nextCursor="demo" />
        <DataTable label="Leer" rows={[]} rowKey={() => ''} empty="Keine Kisten für diese Filter. Filter zurücksetzen" columns={[{ key: 'a', header: 'Name', cell: () => null }]} />
      </section>

      <section className="stack">
        <h2>Reiter und Hinweise</h2>
        <Tabs
          tabs={[
            { key: 'inhalt', label: 'Inhalt', count: 12 },
            { key: 'verlauf', label: 'Verlauf', count: 3 },
          ]}
          active={tab}
          onChange={setTab}
        />
        <Notice tone="warn">Mojang-Prüfung ist aus. Verknüpfte UUIDs sind nicht verifiziert.</Notice>
        <ErrorNotice error={new ApiError(409, 'CONTENT_VERSION_CONFLICT', 'Der Inhalt wurde inzwischen geändert.', {}, '0199c6f2-7b1e-7cc1-9a51-3f0d2b8e4a10')} onRetry={() => {}} />
      </section>

      <ConfirmDialog
        open={confirm}
        title="Kiste löschen"
        consequence="Löscht „Erze“ von Steve mit 12 Item-Einträgen. Nicht umkehrbar."
        confirmText="Erze"
        onConfirm={async () => toast('Kiste gelöscht.')}
        onClose={() => setConfirm(false)}
      />
    </main>
  );
}
